import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../components/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import ubicacionService, { etiquetaUbicacionSeleccion } from '../../services/ubicacionService';
import produccionProgramacionService, {
  ProduccionProgramacionFaltante,
  ProduccionProgramacionPreview,
  ProduccionProgramacionPreviewLinea,
} from '../../services/produccionProgramacionService';
import produccionService from '../../services/produccionService';
import { getApiErrorMessage } from '../../utils/apiError';

const STORAGE_KEY = 'eone.produccion.programacion.preview';
const DEPOSITOS_COOKIE = 'eone.produccion.programacion.preview.depositos';
const OBSERVACION = 'Generada desde programación de producción';

const depositoValido = (valor: string) => {
  const n = Number(valor);
  return Number.isInteger(n) && n >= 1 && n <= 255;
};

const claveDepositos = (username: string, tenantId: number | null) => {
  const identidad = `${tenantId ?? 0}_${username.trim().toLowerCase()}`.replace(/[^a-z0-9_-]/g, '_');
  return `${DEPOSITOS_COOKIE}.${identidad}`;
};

const leerCookie = (nombre: string) => {
  const prefijo = `${nombre}=`;
  const hallada = document.cookie.split('; ').find((parte) => parte.startsWith(prefijo));
  return hallada ? decodeURIComponent(hallada.slice(prefijo.length)) : null;
};

const guardarCookie = (nombre: string, valor: string) => {
  const unAnio = 60 * 60 * 24 * 365;
  document.cookie = `${nombre}=${encodeURIComponent(valor)}; Path=/; Max-Age=${unAnio}; SameSite=Lax`;
};

const leerDepositosCookie = (username: string, tenantId: number | null) => {
  const vacio = { origen: '', destino: '' };
  if (!username.trim() || typeof document === 'undefined') return vacio;
  const raw = leerCookie(claveDepositos(username, tenantId));
  if (!raw) return vacio;
  try {
    const data = JSON.parse(raw) as { origen?: unknown; destino?: unknown };
    return {
      origen: typeof data.origen === 'string' && depositoValido(data.origen) ? data.origen : '',
      destino: typeof data.destino === 'string' && depositoValido(data.destino) ? data.destino : '',
    };
  } catch {
    return vacio;
  }
};

const claveOrden = (codigoArticulo: string, cantidad: number) => `${codigoArticulo}|${cantidad}`;

const etiquetaInsumos = (estado?: string) => {
  switch (estado) {
    case 'Cubierto': return 'Cubierto';
    case 'FaltanteParcial': return 'Faltante parcial';
    case 'SinStock': return 'Sin stock';
    case 'RecetaInvalida': return 'Receta inválida';
    default: return 'Sin calcular';
  }
};

const ProgramacionProduccionPreview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId } = useAuth();
  const username = user?.username ?? '';
  const identidad = `${tenantId ?? 0}:${username.trim().toLowerCase()}`;
  const [lineas, setLineas] = useState<ProduccionProgramacionPreviewLinea[]>([]);
  const [depositos, setDepositos] = useState<{ value: string; label: string }[]>([]);
  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [depositosListos, setDepositosListos] = useState(false);
  const [identidadAplicada, setIdentidadAplicada] = useState('');
  const [fecha, setFecha] = useState('');
  const [preview, setPreview] = useState<ProduccionProgramacionPreview | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [creando, setCreando] = useState(false);
  const creandoRef = useRef(false);
  const [creadasKeys, setCreadasKeys] = useState<Set<string>>(() => new Set());
  const [resultado, setResultado] = useState<{ creadas: number[]; errores: string[] } | null>(null);

  useEffect(() => {
    const desdeEstado = (location.state as { lineas?: ProduccionProgramacionPreviewLinea[] } | null)?.lineas;
    if (desdeEstado && desdeEstado.length > 0) {
      setLineas(desdeEstado);
    } else {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const guardadas = raw ? JSON.parse(raw) as ProduccionProgramacionPreviewLinea[] : [];
        setLineas(Array.isArray(guardadas) ? guardadas : []);
      } catch {
        setLineas([]);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const guardados = leerDepositosCookie(username, tenantId);
    setOrigen(guardados.origen);
    setDestino(guardados.destino);
    setIdentidadAplicada(identidad);
    setDepositosListos(true);
  }, [identidad, tenantId, username]);

  useEffect(() => {
    if (!depositosListos || identidadAplicada !== identidad) return;
    guardarCookie(claveDepositos(username, tenantId), JSON.stringify({ origen, destino }));
  }, [depositosListos, destino, identidad, identidadAplicada, origen, tenantId, username]);

  useEffect(() => {
    const primera = lineas.find((l) => l.fechaSugerida)?.fechaSugerida;
    if (primera && !fecha) setFecha(primera.slice(0, 10));
  }, [fecha, lineas]);

  useEffect(() => {
    ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 200 }).then((rows) => {
      setDepositos(rows.map((u) => ({ value: String(u.nroUbicacion), label: etiquetaUbicacionSeleccion(u) })));
    }).catch(() => setDepositos([]));
  }, []);

  const puedeConsultar = origen !== '' && destino !== '' && lineas.length > 0;
  const ordenesGenerables = useMemo(
    () => (preview?.ordenes ?? []).filter((o) => o.puedeGenerar && o.cantidad > 0),
    [preview],
  );
  const ordenesPendientes = useMemo(
    () => ordenesGenerables.filter((o) => !creadasKeys.has(claveOrden(o.codigoArticulo, o.cantidad))),
    [ordenesGenerables, creadasKeys],
  );
  const yaGeneradas = ordenesGenerables.length > 0 && ordenesPendientes.length === 0;

  const handleFactibilidad = async () => {
    const nroOrigen = Number(origen);
    const nroDestino = Number(destino);
    if (!Number.isInteger(nroOrigen) || nroOrigen < 1 || nroOrigen > 255 || !Number.isInteger(nroDestino) || nroDestino < 1 || nroDestino > 255) {
      enqueueSnackbar('El depósito origen y el destino deben estar entre 1 y 255.', { variant: 'warning' });
      return;
    }
    try {
      setConsultando(true);
      setResultado(null);
      const data = await produccionProgramacionService.previewOrdenes({
        nroDepositoOrigen: nroOrigen,
        nroDepositoDestino: nroDestino,
        fecha: fecha || null,
        lineas: lineas.map((l) => ({
          codigoArticulo: l.codigoArticulo,
          cantidad: l.cantidad,
          fechaSugerida: l.fechaSugerida,
        })),
      });
      setPreview(data);
      setCreadasKeys(new Set());
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo calcular la factibilidad.'), { variant: 'error' });
    } finally {
      setConsultando(false);
    }
  };

  const handleCrear = async () => {
    if (creandoRef.current) return;
    if (!preview || ordenesPendientes.length === 0) {
      enqueueSnackbar(
        yaGeneradas
          ? 'Esas órdenes ya se generaron. No se vuelven a crear.'
          : 'No hay órdenes que se puedan generar. Revise las recetas sin batch.',
        { variant: 'warning' },
      );
      return;
    }
    const nroOrigen = Number(origen);
    const nroDestino = Number(destino);
    const pendientes = ordenesPendientes;
    creandoRef.current = true;
    setCreando(true);
    const creadas: number[] = [];
    const errores: string[] = [];
    const clavesOk: string[] = [];
    try {
      for (const orden of pendientes) {
        try {
          const creada = await produccionService.create({
            codigoArticulo: orden.codigoArticulo,
            cantidad: orden.cantidad,
            depositoOrigen: nroOrigen,
            depositoDestino: nroDestino,
            fechaInicio: (fecha || orden.fechaInicio).slice(0, 10),
            observaciones: OBSERVACION,
          });
          creadas.push(creada.codigoProduccion);
          clavesOk.push(claveOrden(orden.codigoArticulo, orden.cantidad));
        } catch (error) {
          errores.push(`${orden.codigoArticulo}: ${getApiErrorMessage(error, 'No se pudo crear la orden')}`);
        }
      }
      if (clavesOk.length > 0) {
        const next = new Set<string>();
        creadasKeys.forEach((clave) => next.add(clave));
        clavesOk.forEach((clave) => next.add(clave));
        setCreadasKeys(next);
      }
      setResultado({ creadas, errores });
      if (creadas.length > 0) {
        enqueueSnackbar(`Órdenes creadas en borrador: Nro ${creadas.join(', Nro ')}.`, { variant: 'success' });
      }
      if (errores.length > 0) {
        enqueueSnackbar(
          creadas.length > 0
            ? `Quedaron creadas las órdenes anteriores. Fallaron ${errores.length}.`
            : 'No se pudo crear ninguna orden.',
          { variant: 'error' },
        );
      }
    } finally {
      creandoRef.current = false;
      setCreando(false);
    }
  };

  const handleExportarFaltantes = () => {
    const faltantes: ProduccionProgramacionFaltante[] = preview?.faltantes ?? [];
    if (faltantes.length === 0) {
      enqueueSnackbar('No hay faltantes para exportar.', { variant: 'info' });
      return;
    }
    const rows = faltantes.map((f) => ({
      Codigo: f.codigoComponente,
      Descripcion: f.descripcion,
      Faltante: f.cantidadFaltante,
      CodProveedor: f.codProveedor ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Faltantes');
    XLSX.writeFile(wb, `FaltantesProgramacion_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/produccion/programacion')}>
                  Volver
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Órdenes en borrador
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportarFaltantes} disabled={!preview}>
                  Excel de faltantes
                </Button>
                <Button variant="outlined" component={RouterLink} to="/compras/informes/sugerencia-compra">
                  Sugerencia de compra
                </Button>
              </Stack>
            </Stack>

            <Alert severity="info">
              Las órdenes se crean en Borrador, una por una, y no mueven stock. Un faltante no impide el alta.
              Si una orden falla, las anteriores quedan creadas. La receta sin batch válido no se convierte en orden.
            </Alert>
            {lineas.length === 0 && (
              <Alert severity="warning">No hay artículos seleccionados. Vuelva a la programación y elija las filas.</Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SearchableSelect label="Depósito origen" value={origen || null} onChange={(v) => { setOrigen(v ?? ''); setPreview(null); }} options={depositos} />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect label="Depósito destino" value={destino || null} onChange={(v) => setDestino(v ?? '')} options={depositos} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fecha de inicio"
                  type="date"
                  value={fecha}
                  onChange={(e) => { setFecha(e.target.value); setPreview(null); }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => void handleFactibilidad()} disabled={!puedeConsultar || consultando || creando}>
                {consultando ? 'Calculando…' : 'Ver factibilidad'}
              </Button>
              <Button variant="contained" onClick={() => void handleCrear()} disabled={creando || ordenesPendientes.length === 0}>
                {creando ? 'Creando…' : yaGeneradas ? 'Ya generadas' : `Crear borradores (${ordenesPendientes.length})`}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/produccion/ordenes')}>
                Ir a órdenes
              </Button>
              {(consultando || creando) && <CircularProgress size={22} />}
            </Stack>

            {yaGeneradas && !resultado && (
              <Alert severity="info">
                Esas órdenes ya se generaron en borrador. Un nuevo clic no vuelve a crearlas.
              </Alert>
            )}

            {resultado && (
              <Alert severity={resultado.errores.length > 0 ? 'warning' : 'success'}>
                {resultado.creadas.length > 0
                  ? `Órdenes creadas en borrador: Nro ${resultado.creadas.join(', Nro ')}. `
                  : 'No se creó ninguna orden. '}
                {resultado.errores.length > 0
                  ? `Las que fallaron no revierten las anteriores. ${resultado.errores.join(' · ')}`
                  : 'No se movió stock.'}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artículo</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Insumos</TableCell>
                  <TableCell>Puede generar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(preview?.ordenes ?? lineas.map((l) => ({
                  codigoArticulo: l.codigoArticulo,
                  descripcion: l.descripcion,
                  cantidad: l.cantidad,
                  estadoInsumos: '',
                  puedeGenerar: false,
                  motivo: 'Presione Ver factibilidad.',
                  insumos: [],
                }))).map((orden) => (
                  <TableRow key={orden.codigoArticulo}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{orden.codigoArticulo}</Typography>
                      <Typography variant="caption" color="text.secondary">{orden.descripcion}</Typography>
                    </TableCell>
                    <TableCell align="right">{orden.cantidad}</TableCell>
                    <TableCell>
                      {preview ? <Chip size="small" label={etiquetaInsumos(orden.estadoInsumos)} /> : '—'}
                    </TableCell>
                    <TableCell>
                      {preview
                        ? (orden.puedeGenerar ? 'Sí' : orden.motivo || 'No')
                        : 'Pendiente'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {(preview?.faltantes.length ?? 0) > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Faltantes de insumos</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Este listado no escribe la sugerencia de compra.{' '}
                  <Link component={RouterLink} to="/compras/informes/sugerencia-compra">Abrir sugerencia de compra</Link>
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Faltante</TableCell>
                      <TableCell>Proveedor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview?.faltantes.map((faltante) => (
                      <TableRow key={faltante.codigoComponente}>
                        <TableCell>{faltante.codigoComponente}</TableCell>
                        <TableCell>{faltante.descripcion}</TableCell>
                        <TableCell align="right">{faltante.cantidadFaltante}</TableCell>
                        <TableCell>{faltante.codProveedor ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProgramacionProduccionPreview;
